/**
 * Computes the Internal Rate of Return (IRR) using the Newton-Raphson method.
 *
 * IRR is the discount rate r that satisfies NPV = 0:
 *   Σ [ cashFlows[t] / (1 + r)^t ] = 0
 *
 * Unlike CAGR — which only considers a start value and end value — true IRR
 * accounts for the timing of every intermediate cash flow (e.g. annual
 * interest payments), giving a more accurate annualised return figure.
 *
 * @param {number[]} cashFlows
 *   Array of cash flows indexed by time period (year).
 *   cashFlows[0] is t=0 (typically negative — initial outlay).
 *   e.g. [-180000, -16000, -16000, -16000, 484000]
 *
 * @param {object}  [options]
 * @param {number}  [options.initialGuess=0.1]  Starting estimate for r (decimal, e.g. 0.1 = 10%).
 * @param {number}  [options.maxIterations=100] Maximum Newton-Raphson iterations before giving up.
 * @param {number}  [options.tolerance=1e-7]    Convergence threshold on Δr.
 *
 * @returns {number|null}  IRR as a decimal (e.g. 0.0731 for 7.31%), or null if no convergence.
 */
export const computeIRR = (cashFlows, { initialGuess = 0.1, maxIterations = 100, tolerance = 1e-7 } = {}) => {
  if (!cashFlows || cashFlows.length < 2) return null;

  // IRR only exists when cash flows change sign at least once
  const hasPositive = cashFlows.some(cf => cf > 0);
  const hasNegative = cashFlows.some(cf => cf < 0);
  if (!hasPositive || !hasNegative) return null;

  let r = initialGuess;

  for (let iter = 0; iter < maxIterations; iter++) {
    let npv = 0;
    let dnpv = 0; // dNPV/dr — used for Newton-Raphson step

    for (let t = 0; t < cashFlows.length; t++) {
      const discount = Math.pow(1 + r, t);
      npv += cashFlows[t] / discount;
      dnpv -= (t * cashFlows[t]) / (discount * (1 + r));
    }

    // Avoid division by near-zero derivative
    if (Math.abs(dnpv) < 1e-12) break;

    const delta = npv / dnpv;
    r -= delta;

    if (Math.abs(delta) < tolerance) {
      return isFinite(r) ? r : null;
    }
  }

  return null; // did not converge within maxIterations
};

/**
 * Builds the premium-financing cash flow schedule for a given surrender year
 * and returns the true IRR via Newton-Raphson.
 *
 * Cash flow model:
 *
 *   t = 0         : -base
 *                   Own cash paid in. `base` is already net of first-year bonus.
 *
 *   t = 1 .. year : -annualLoanInterest[t]
 *                   Annual interest on the bank loan (fixed or per-year override).
 *                   Any termAddOn cost for that year is added on top.
 *
 *   t = year      : +cashValue − bankLoan − annualLoanInterest[year]
 *                   Policy surrender value, minus loan repayment, minus final year interest.
 *
 * Relationship to existing variables in calculator.js:
 *   bankLoan = principal − base − firstYearBonus
 *
 * @param {object}   params
 * @param {number}   params.base                 Own cash paid at t=0.
 * @param {number}   params.principal            Total policy premium.
 * @param {number}   params.firstYearBonus       First-year bonus / premium discount (received at t=0).
 * @param {number}   params.cashValue            Policy surrender / cash value at target year.
 * @param {number}   params.bankLoanInterest     Fixed annual loan interest rate (%).
 * @param {boolean}  params.overrideLoanInterest Whether per-year rates are active.
 * @param {number[]} params.termLoanPercentage   Per-year loan rates (%) when overriding.
 * @param {number[]} params.termAddOn            Per-year fixed add-on costs when overriding.
 * @param {number}   params.year                 Target year index (≥ 1).
 *
 * @returns {number|null}  True IRR as a percentage (e.g. 7.31 for 7.31%), or null if undetermined.
 */
export const getPremiumFinancingIRR = ({
  base,
  principal,
  firstYearBonus,
  cashValue,
  bankLoanInterest,
  overrideLoanInterest,
  termLoanPercentage,
  termAddOn,
  year,
}) => {
  const bonus = firstYearBonus ?? 0;
  const bankLoan = (principal ?? 0) - (base ?? 0) - bonus;

  if (!base || !cashValue || bankLoan < 0 || year < 1) return null;

  // Per-year loan interest rates for years 1..year
  const loanRates =
    overrideLoanInterest && termLoanPercentage
      ? termLoanPercentage.slice(0, year)
      : Array.from({ length: year }, () => bankLoanInterest ?? 0);

  // Per-year fixed add-on costs for years 1..year
  const addOns =
    overrideLoanInterest && termAddOn
      ? termAddOn.slice(0, year)
      : Array.from({ length: year }, () => 0);

  // t=0: base is already net of first-year bonus in the premium financing inputs.
  const cashFlows = [-base];

  for (let t = 1; t <= year; t++) {
    const annualInterest = bankLoan * ((loanRates[t - 1] ?? 0) / 100) + (Number(addOns[t - 1]) || 0);

    if (t < year) {
      // Intermediate years: only loan interest is paid
      cashFlows.push(-annualInterest);
    } else {
      // Terminal year: surrender policy, repay bank loan, pay final interest
      cashFlows.push(cashValue - bankLoan - annualInterest);
    }
  }

  const irr = computeIRR(cashFlows);
  return irr !== null ? irr * 100 : null;
};
